/**
 * PropertyAccessForm — collapsible "How will the verifier get in?" block
 * used in Screen 2 of the booking wizard.
 *
 * Defaults closed — owners with simple cases skip it entirely. Owners with
 * caretakers / tenants / specific access windows expand and fill in.
 */

import React, { useState } from 'react';
import {
  Box,
  Collapse,
  Typography,
  TextField,
  MenuItem,
  Button,
  Stack,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

const WHO_IS_THERE_OPTIONS = [
  { value: '', label: 'Not specified' },
  { value: 'none', label: 'Nobody — property is vacant' },
  { value: 'caretaker', label: 'Caretaker / property manager' },
  { value: 'tenant', label: 'Tenant or current occupant' },
  { value: 'family', label: 'Family member' },
];

const PropertyAccessForm = ({ value = {}, onChange }) => {
  const [open, setOpen] = useState(!!(value && Object.values(value).some((v) => v)));

  const patch = (field, fieldValue) => {
    onChange({ ...value, [field]: fieldValue });
  };

  return (
    <Box>
      <Button
        type="button"
        onClick={() => setOpen((o) => !o)}
        endIcon={open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        sx={{ textTransform: 'none', fontWeight: 600 }}
      >
        {open ? 'Hide access details' : 'Add access details (optional)'}
      </Button>

      <Collapse in={open}>
        <Stack spacing={2} sx={{ mt: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Help the verifier get in. All fields are optional — leave blank if not relevant.
          </Typography>

          <TextField
            select
            fullWidth
            label="Who's at the property?"
            value={value.whoIsThere || ''}
            onChange={(e) => patch('whoIsThere', e.target.value)}
          >
            {WHO_IS_THERE_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </TextField>

          {(value.whoIsThere && value.whoIsThere !== 'none') && (
            <>
              <TextField
                fullWidth
                label="Contact name"
                value={value.contactName || ''}
                onChange={(e) => patch('contactName', e.target.value)}
                placeholder="e.g. Mr. Moyo"
              />
              <TextField
                fullWidth
                label="Contact phone"
                value={value.contactPhone || ''}
                onChange={(e) => patch('contactPhone', e.target.value)}
                placeholder="+263 77 123 4567"
              />
            </>
          )}

          <TextField
            fullWidth
            multiline
            minRows={2}
            label="Access instructions (keys, gate code, etc.)"
            value={value.accessInstructions || ''}
            onChange={(e) => patch('accessInstructions', e.target.value)}
            placeholder="Gate code 4521. Front door key under the third plant pot."
          />

          <TextField
            fullWidth
            label="Best time to visit"
            value={value.viewingWindow || ''}
            onChange={(e) => patch('viewingWindow', e.target.value)}
            placeholder="Weekdays 9–4, weekends after lunch"
          />
        </Stack>
      </Collapse>
    </Box>
  );
};

export default PropertyAccessForm;
